import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Card, CardContent, CircularProgress, Container, Divider, Grid, Typography } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { FieldErrors, Resolver, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { usePaymentGatewayPublicKey } from '~/hooks/usePaymentGatewayPublicKey';
import api from '~/services/api';
import { storeState } from '~/store';
import { useRenewalStore } from '~/store/slices/renewal';
import { toastFromApiNotification } from '~/utils/backendErrorMessages';
import { get422ErrorMessage, getErrorMessage, logErrorWithContext } from '~/utils/errorHandler';
import { reportError } from '~/utils/reportError';
import { container, headerContainer } from '../styles';
import { ContractRenewalPayload, CreateTokenCardResponse } from './interfaces/card-token-response';
import { Cards, Contract, Product } from './interfaces/contract';
import { ContractInfo, Data } from './interfaces/contractinfo';
import { FinancialData } from './interfaces/financial-data';
import { AddressCard, AddressInputs } from './partials/address';
import { AuthRenewal } from './partials/auth';
import { BillingAddressCard } from './partials/BillingAddressCard.tsx';
import { ContractCard } from './partials/contract';
import { EquipmentCard } from './partials/equipment';
import { InsuredInfoCard } from './partials/insured-info';
import { CardData, PaymentCard } from './partials/payment';
import { Resume } from './partials/resume';
import { UpsellCard } from './partials/upsell';
import { cardSchema } from './validations';

interface RenewalResponseData {
  contract_id: string;
  contract_code: string;
  is_renew_outdated: boolean;
}

interface RenewalResponse {
  data: RenewalResponseData;
  notifications: any[];
}

const PAGARME_URL = import.meta.env.VITE_PAGARME_API_URL;

/** ID da organização Aride – usado como padrão no fluxo de renovação quando `org` não vem na URL */
const RENEWAL_DEFAULT_ORG_ID = 'rw0Om';

/** GET /Contracts/:id pode vir como `{ data: Contract }` ou com o contrato no root — normaliza para `Data`. */
function normalizeContractApiBody(body: ContractInfo | Data | undefined): Data | undefined {
  if (!body || typeof body !== 'object') return undefined;
  if ('data' in body && body.data && typeof body.data === 'object' && 'contract_id' in (body.data as object)) {
    return body.data as Data;
  }
  if ('contract_id' in body) {
    return body as Data;
  }
  return undefined;
}

export default function ContractRenewal() {
  const { isAuthenticated, setAuthenticated } = useRenewalStore();

  const [contractData, setContractData] = useState<Contract | null>(null);
  const [products, setProducts] = useState<number[]>([]);
  const [financialInfos, setFinancialInfos] = useState<FinancialData>();
  const [updateDataLoading, setUpdateDataLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitFormState, setSubmitFormState] = useState({ loading: false, success: false });
  const [renewalResponseData, setRenewalResponseData] = useState<RenewalResponseData | null>(null);
  const [contractInfo, setContractInfo] = useState<ContractInfo>();
  const [contractInfoError, setContractInfoError] = useState<boolean>(false);
  const { contractId } = useParams();
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get('org') ?? RENEWAL_DEFAULT_ORG_ID;
  const { publicKey: paymentGatewayPublicKey, loading: _loadingPublicKey } = usePaymentGatewayPublicKey(organizationId);
  const [billingAddress, setBillingAddress] = useState<AddressInputs | null>(null);
  const [cards, setCards] = useState<Cards[]>([]);
  const [selectedCardId, setSelectedCardId] = useState<string>('');

  const navigate = useNavigate();

  const withOrgQuery = (path: string) => {
    if (!organizationId) return path;
    const params = new URLSearchParams();
    params.set('organization_id', organizationId);
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}${params.toString()}`;
  };

  const conditionalResolver: Resolver<CardData> = useCallback(
    async (values, context, options) => {
      if (selectedCardId !== 'new') {
        return { values, errors: {} };
      }
      return yupResolver(cardSchema)(values, context, options);
    },
    [selectedCardId]
  );

  const {
    control: cardControl,
    handleSubmit,
    unregister,
    formState: { errors }
  } = useForm<CardData>({
    resolver: conditionalResolver,
    mode: 'onChange',
    defaultValues: {
      card_number: '',
      card_holder_name: '',
      card_expiration_date: '',
      card_cvv: ''
    },
    shouldUnregister: true
  });

  const watchedCardNumber = useWatch({ control: cardControl, name: 'card_number', defaultValue: '' });
  const watchedCardHolderName = useWatch({ control: cardControl, name: 'card_holder_name', defaultValue: '' });
  const watchedCardExpiry = useWatch({ control: cardControl, name: 'card_expiration_date', defaultValue: '' });
  const watchedCardCvv = useWatch({ control: cardControl, name: 'card_cvv', defaultValue: '' });

  const isNewCardFormValid =
    selectedCardId !== 'new' ||
    (() => {
      try {
        return cardSchema.isValidSync({
          card_number: watchedCardNumber ?? '',
          card_holder_name: watchedCardHolderName ?? '',
          card_expiration_date: watchedCardExpiry ?? '',
          card_cvv: watchedCardCvv ?? ''
        });
      } catch {
        return false;
      }
    })();

  const updateAddress = async (data: AddressInputs) => {
    const payload = organizationId ? { ...data, organization_id: organizationId } : data;
    await api.v2?.post(`Contracts/${contractId}/insured_items/address`, payload);

    setContractData((prev) => (prev ? { ...prev, address: { ...prev.address, ...data } } : prev));

    setBillingAddress(data);
  };

  const getContractInfos = async () => {
    try {
      const url = withOrgQuery(`Contracts/${contractId}`);
      const response = await api.v2?.get<ContractInfo | Data>(url);
      const raw = response?.data;
      const normalized = normalizeContractApiBody(raw as ContractInfo | Data);
      setContractInfo(normalized ? { data: normalized } : (raw as ContractInfo));
      setContractInfoError(false);
      return raw;
    } catch (error: any) {
      logErrorWithContext(error, 'contract_renewal_get_contract_infos', { contractId });
      reportError('Erro ao buscar informações do contrato para renovação', error, {
        context: 'contract_renewal_get_contract_infos',
        contractId,
        errorType: 'renewal_contract_info_fetch_error'
      });
      setContractInfoError(true);
      setContractInfo(undefined);
      return undefined;
    }
  };

  const fetchData = async () => {
    setLoading(true);
    const [contractInfoResponse] = await Promise.all([getContractInfos()]);
    const quotesUrl = withOrgQuery(`Contracts/${contractId}/quotes`);

    try {
      const { data: responseData } = await (api.v2 ?? api).get(quotesUrl);
      const data = responseData?.data ?? responseData;
      const prods = [...data.contract_item, ...data.upsell];
      const quotePlanDetails = data.plan_details;
      const contractPayload = normalizeContractApiBody(contractInfoResponse as ContractInfo | Data | undefined);
      const contractPlanDetails = contractPayload?.plan;

      const flatRateFromQuote = data.contract_details.flat_rate;
      const clubValueFallback = contractPlanDetails?.club_value;
      const flatRateResolved =
        flatRateFromQuote != null && Number(flatRateFromQuote) > 0
          ? Number(flatRateFromQuote)
          : contractPayload?.flat_rate != null && Number(contractPayload.flat_rate) > 0
          ? Number(contractPayload.flat_rate)
          : clubValueFallback != null && Number(clubValueFallback) > 0
          ? Number(clubValueFallback)
          : Number(flatRateFromQuote ?? contractPayload?.flat_rate ?? clubValueFallback ?? 0);

      setContractData(data);
      setProducts(prods.filter((p: Product) => p.is_checked).map((p: Product) => p.product_id));
      setFinancialInfos({
        financial_charges: data.contract_details.financial_charges,
        subtotal: data.contract_details.subtotal,
        total: data.contract_details.total,
        installments: data.contract_details.installments,
        flat_rate: flatRateResolved,
        type_club: quotePlanDetails?.type_club ?? contractPlanDetails?.type_club ?? undefined,
        plan_club_name: quotePlanDetails?.plan_club_name ?? contractPlanDetails?.club_name ?? undefined,
        plan_has_club: quotePlanDetails?.plan_has_club ?? contractPlanDetails?.has_club ?? false,
        plan_club_description:
          quotePlanDetails?.plan_club_description ?? contractPlanDetails?.club_description ?? undefined
      });
      getCards(data.insured.id);
    } catch (error: any) {
      logErrorWithContext(error, 'contract_renewal_fetch_data', { contractId });

      const hasNotifications = error.response?.data?.notifications && Array.isArray(error.response.data.notifications);

      reportError('Erro ao buscar dados da cotação para renovação de contrato', error, {
        context: 'contract_renewal_fetch_data',
        contractId,
        hasNotifications,
        notifications: hasNotifications ? error.response.data.notifications : [],
        errorType: 'renewal_quote_fetch_error'
      });

      // Verificar se há notificações no erro da API
      if (hasNotifications) {
        const notifications = error.response.data.notifications;

        // Exibir cada notification como um popup separado
        notifications.forEach((notification: { key?: string; message?: string }, index: number) => {
          setTimeout(() => {
            storeState.addToast(toastFromApiNotification(notification));
          }, index * 100);
        });
      } else {
        const errorResponse = getErrorMessage(
          error,
          'Não foi possível carregar os dados do contrato. Tente novamente mais tarde.'
        );
        storeState.addToast(errorResponse);
      }
    } finally {
      setLoading(false);
    }
  };

  const getCards = async (id: string) => {
    const url = withOrgQuery(`Customers/${id}/cards`);
    const cardsRes = await (api.v2 ?? api).get(url);
    setCards(cardsRes.data?.data ?? cardsRes.data);
  };

  const updateQuoteData = async ({ prods = products }: { prods?: number[] }) => {
    const oldProducts = [...products];
    const oldFinancialInfos = financialInfos;

    try {
      setUpdateDataLoading(true);
      const payload: { products: { product_id: number }[]; organization_id?: string } = {
        products: prods.map((id) => ({ product_id: id }))
      };
      if (organizationId) payload.organization_id = organizationId;
      const { data: responseData } = await (api.v2 ?? api).put(
        `/Contracts/quotes/${contractData?.contract_details.quote_id}`,
        payload
      );
      const data = responseData?.data ?? responseData;

      setFinancialInfos({
        financial_charges: data.financial_charges,
        subtotal: data.subtotal,
        total: data.total,
        installments: data.installments,
        flat_rate:
          data.flat_rate != null && Number(data.flat_rate) > 0 ? Number(data.flat_rate) : financialInfos?.flat_rate,
        type_club: financialInfos?.type_club,
        plan_club_name: financialInfos?.plan_club_name,
        plan_has_club: financialInfos?.plan_has_club,
        plan_club_description: financialInfos?.plan_club_description
      });
    } catch (e: any) {
      setProducts(oldProducts);
      setFinancialInfos(oldFinancialInfos);
      logErrorWithContext(e, 'contract_renewal_update_quote', { contractId });
      reportError('Erro ao atualizar cotação durante renovação de contrato', e, {
        context: 'contract_renewal_update_quote',
        contractId,
        quoteId: contractData?.contract_details.quote_id,
        products: prods,
        errorType: 'renewal_quote_update_error'
      });
      const errorResponse = getErrorMessage(e, 'Não foi possível atualizar a sua cotação. Tente novamente mais tarde.');
      storeState.addToast(errorResponse);
    } finally {
      setUpdateDataLoading(false);
    }
  };

  const handleProductChange = async (productId: number) => {
    const oldProducts = [...products];
    let newProducts = products.includes(productId) ? products.filter((p) => p !== productId) : [...products, productId];

    try {
      setProducts(newProducts);
      if (!newProducts.length) return;
      await updateQuoteData({ prods: newProducts });
    } catch {
      setProducts(oldProducts);
    }
  };

  useEffect(() => {
    if (selectedCardId !== 'new') {
      unregister(['card_number', 'card_holder_name', 'card_expiration_date', 'card_cvv']);
    }
  }, [selectedCardId, unregister]);

  const handleSubmitForm = async (cardData: CardData) => {
    setSubmitFormState((prev) => ({ ...prev, loading: true }));

    if (!selectedCardId) {
      storeState.addToast({ message: 'Escolha uma forma de pagamento.', type: 'error', title: 'Pagamento' });
      document.getElementById('payment-card')?.scrollIntoView({ behavior: 'smooth' });
      setSubmitFormState((prev) => ({ ...prev, loading: false }));
      return;
    }

    const payload: ContractRenewalPayload = {
      tax_address: billingAddress ?? {
        street: '',
        number: '',
        additional_details: '',
        zipcode: '',
        neighborhood: '',
        city: '',
        state: ''
      }
    };

    try {
      if (selectedCardId === 'new') {
        if (!paymentGatewayPublicKey) {
          storeState.addToast({
            title: 'Configuração de pagamento',
            message:
              'Chave de pagamento da organização não disponível. Acesse com o link da organização (parâmetro org).',
            type: 'error'
          });
          return;
        }
        const [month, year] = cardData.card_expiration_date.split('/');
        const tokenRes = await axios.post<CreateTokenCardResponse>(
          `${PAGARME_URL}/tokens?appId=${paymentGatewayPublicKey}`,
          {
            type: 'card',
            card: {
              number: cardData.card_number.replace(/\s+/g, ''),
              exp_month: parseInt(month),
              exp_year: parseInt(year),
              holder_name:
                typeof cardData.card_holder_name === 'string'
                  ? cardData.card_holder_name.trim()
                  : cardData.card_holder_name,
              cvv: cardData.card_cvv.trim()
            }
          }
        );
        payload.hash_card = tokenRes.data.id;
      } else {
        payload.card_id = selectedCardId;
      }
      if (organizationId) payload.organization_id = organizationId;

      const renewalResponse = await (api.v2 ?? api).put<RenewalResponse>(
        `/Contracts/quotes/${contractData!.contract_details.quote_id}/renew`,
        payload
      );

      setRenewalResponseData(renewalResponse.data.data);
      setSubmitFormState((prev) => ({ ...prev, success: true }));
    } catch (error: any) {
      const errorContext = logErrorWithContext(error, 'contract_renewal_process', {
        contractId,
        quoteId: contractData?.contract_details.quote_id,
        selectedCardId,
        isNewCard: selectedCardId === 'new'
      });

      if (error.isAxiosError && error.response?.status === 422) {
        const axiosError = error as AxiosError<any>;
        const errorResponse = get422ErrorMessage(axiosError);

        storeState.addToast(errorResponse);

        const errors = axiosError?.response?.data?.errors;
        const rollbarContext = errors?.['request.card']
          ? 'contract_renewal_422_card_error'
          : 'contract_renewal_422_general_error';

        reportError(`Erro 422 na renovação: ${errorResponse.title}`, axiosError, {
          status: axiosError.response?.status,
          data: {
            message: axiosError.response?.data.message,
            errors: axiosError.response?.data.errors,
            axiosRaw: axiosError.toJSON()
          },
          context: rollbarContext,
          errorType: errorContext.errorType,
          contractId,
          quoteId: contractData?.contract_details.quote_id
        });
      } else {
        const errorResponse = getErrorMessage(
          error,
          'Não foi possível processar seus dados no momento. Tente novamente mais tarde.'
        );

        storeState.addToast(errorResponse);

        const rollbarContext = error.isAxiosError
          ? error.message === 'Network Error'
            ? 'contract_renewal_networkError'
            : 'contract_renewal_axios_error'
          : 'contract_renewal_non_axios_error';

        reportError(`Erro durante renovação: ${errorResponse.title}`, error, {
          status: error.response?.status,
          data: error.response?.data,
          context: rollbarContext,
          errorType: errorContext.errorType,
          originalMessage: error.message,
          contractId,
          quoteId: contractData?.contract_details.quote_id,
          selectedCardId,
          isNewCard: selectedCardId === 'new',
          hasBillingAddress: !!billingAddress
        });
      }
    } finally {
      setSubmitFormState((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    setAuthenticated(import.meta.env.DEV ? true : false);
    fetchData();
  }, []);

  useEffect(() => {
    if (submitFormState.success) {
      if (renewalResponseData?.is_renew_outdated) {
        const fileParams = new URLSearchParams({ source: 'renewal', cid: renewalResponseData.contract_id });
        if (organizationId) fileParams.set('org', organizationId);
        navigate(`/contract/file?${fileParams.toString()}`);
      } else {
        navigate(`/contract/renewal/success/${contractId}${organizationId ? `?org=${organizationId}` : ''}`);
      }
    }
  }, [submitFormState.success, renewalResponseData, navigate, contractId, organizationId]);

  useEffect(() => {
    const cardFields = [
      'card_number',
      'card_holder_name',
      'card_expiration_date',
      'card_cvv'
    ] as (keyof FieldErrors<CardData>)[];
    const hasCardErrors = cardFields.some((field) => !!errors[field]);

    if (selectedCardId === 'new' && hasCardErrors) {
      const element = document.getElementById('payment-card');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [errors, selectedCardId]);

  if (loading) {
    return (
      <CircularProgress
        sx={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)'
        }}
      />
    );
  }

  return (
    <Container maxWidth="lg" sx={container}>
      <Grid container spacing={2} justifyContent={'center'}>
        <Grid item md={8} xs={12}>
          <Card>
            {contractInfoError ? (
              <CardContent sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography variant="h6" color="error" gutterBottom>
                    Não foi possível recuperar as informações do contrato
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                    Houve um problema ao carregar os dados do seu contrato. Por favor, entre em contato com nosso
                    suporte para obter assistência.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    ID do Contrato: {contractId}
                  </Typography>
                </Box>
              </CardContent>
            ) : !isAuthenticated && contractInfo?.data ? (
              <AuthRenewal data={contractInfo?.data} />
            ) : (
              <>
                <CardContent sx={{ padding: { xs: '0.5rem', sm: '1rem' } }}>
                  <Box sx={headerContainer}>
                    <Typography variant="h6" align="left">
                      Detalhes do contrato para renovação
                    </Typography>
                  </Box>
                  <Divider variant="middle" color="#a547ff" sx={{ height: '3px' }} />
                  <Resume financialData={financialInfos} isLoading={updateDataLoading} />
                  <EquipmentCard updateQuoteData={updateQuoteData} insured_item={contractData?.insured_item} />
                  {contractInfo?.data?.insured && (
                    <InsuredInfoCard
                      quoteId={contractData?.contract_details.quote_id}
                      insuredInfo={contractInfo?.data?.insured}
                      organizationId={organizationId}
                    />
                  )}
                  <AddressCard address={contractData?.address} updateAddress={updateAddress} />
                  {contractData?.address && (
                    <BillingAddressCard
                      mainAddress={contractData?.address}
                      onBillingAddressChange={(data) => setBillingAddress(data)}
                    />
                  )}
                  <form onSubmit={handleSubmit(handleSubmitForm)}>
                    <PaymentCard
                      savedCards={cards}
                      control={cardControl}
                      errors={errors}
                      selectedCardId={selectedCardId}
                      setSelectedCardId={setSelectedCardId}
                    />
                    {contractData?.upsell !== undefined && contractData?.upsell.length > 0 && (
                      <UpsellCard
                        products={products}
                        upsell={contractData?.upsell}
                        handleProductChange={handleProductChange}
                      />
                    )}
                    <ContractCard
                      financialData={financialInfos}
                      isLoading={updateDataLoading}
                      products={products}
                      handleProductChange={handleProductChange}
                      contract_item={contractData?.contract_item}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <LoadingButton
                        variant="contained"
                        disabled={
                          !products.length ||
                          loading ||
                          updateDataLoading ||
                          submitFormState.loading ||
                          (selectedCardId === 'new' && !isNewCardFormValid)
                        }
                        type="submit"
                        sx={{ minWidth: '60%' }}
                      >
                        {submitFormState.loading ? <CircularProgress size={24} /> : 'Quero renovar meu contrato'}
                      </LoadingButton>
                    </Box>
                  </form>
                </CardContent>
              </>
            )}
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
